import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { 
  createProject, getProjects, updateProject, getProjectStatuses, assignEmployeesToProject, getProjectAssignments,
  createMilestone, getMilestones, deleteProject, assignEmployeesToMilestone, updateMilestone, getMilestoneAssignments,
  getProjectBudget, updateProjectBudget,
  syncMilestones,
  exportProjects,
  createProjectStatus,
  reorderProjectStatuses
} from "../../services/projectService";
import { getProjectLabels, setProjectLabels } from "../../services/labelService";
import { getEmployees } from "../../services/peopleService";
import { getAllClientCompanies, getClientContacts } from "../../services/leadService";
import ui from "./DashboardUI.module.css";
import FormModal from "../../components/forms/FormModal";
import DetailsModal from "../../components/forms/DetailsModal";
import detailStyles from "../../components/forms/DetailsModal.module.css";
import SettingsToggle from "../../components/forms/SettingsToggle";
import DataTable from "../../components/forms/DataTable";
import Toast from "../../components/Toast";
import ColorPicker from "../../components/forms/ColorPicker";
import LabelSelector from "../../components/forms/LabelSelector";
import { useCurrentUser } from "../../context/UserContext";
import { usePermissions } from "../../hooks/usePermission";
import ConfirmDialog from "../../components/ConfirmDialog";
import AddStatusModal from "../../components/AddStatusModal";
import StatusDropdown from "../../components/forms/StatusDropdown";
import CustomDropdown from "../../components/forms/CustomDropdown";
import useMinimumLoader from "../../hooks/useMinimumLoader";
import ReorderListModal from "../../components/ReorderListModal";
import settingsIcon from "../../assets/icons/settings.svg";
import plusIcon from "../../assets/icons/plus.svg";
import GanttView from "../../components/projects/GanttView";

// Project color palette - matches backend PROJECT_COLORS
const PROJECT_COLORS = [
  { value: '#3b82f6', label: 'Blue' },
  { value: '#10b981', label: 'Emerald' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#8b5cf6', label: 'Violet' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#f97316', label: 'Orange' },
  { value: '#84cc16', label: 'Lime' },
  { value: '#6366f1', label: 'Indigo' },
  { value: '#14b8a6', label: 'Teal' },
];

const getRandomProjectColor = () => {
  const randomIndex = Math.floor(Math.random() * PROJECT_COLORS.length);
  return PROJECT_COLORS[randomIndex].value;
};

export default function Projects() {
  const currentUser = useCurrentUser();
  const { hasPermission } = usePermissions();
  const routeLocation = useLocation();

  // Permission checks
  const canViewAllProjects = hasPermission('view_projects');
  const canViewAssignedProjects = hasPermission('view_assigned_projects');
  const canCreateProjects = hasPermission('create_projects');
  const canEditProjects = hasPermission('edit_projects');
  const canDeleteProjects = hasPermission('delete_projects');
  const canManageProjectTeam = hasPermission('manage_project_team');
  const canViewProjectBudgets = hasPermission('view_project_budgets');
  const canManageProjectBudgets = hasPermission('manage_project_budgets');
  const canManageCompanySettings = hasPermission('manage_company_settings');

  const [loading, setLoading] = useState(true);
  const showLoading = useMinimumLoader(loading);
  const [projects, setProjects] = useState([]);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editableProject, setEditableProject] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const settingsRef = useRef();
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [showMilestoneEditModal, setShowMilestoneEditModal] = useState(false);
  const [editableMilestone, setEditableMilestone] = useState(null);
  const [activeMilestoneTab, setActiveMilestoneTab] = useState("Overview");
  const [milestoneEmployeeSearch, setMilestoneEmployeeSearch] = useState("");
  const [milestoneSearchFocused, setMilestoneSearchFocused] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [pendingArchiveAction, setPendingArchiveAction] = useState(null);
  const [showStatusReorderModal, setShowStatusReorderModal] = useState(false);
  const [savingStatusOrder, setSavingStatusOrder] = useState(false);

  // Form state
  const [clientCompany, setClientCompany] = useState("");
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [urgency, setUrgency] = useState("Medium");
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(null);
  const [projectColor, setProjectColor] = useState(() => getRandomProjectColor());
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [projectLocation, setProjectLocation] = useState("");
  const [address, setAddress] = useState("");
  
  // Edit modal labels state
  const [editLabels, setEditLabels] = useState([]);
  
  // Budget state
  const [budgetCost, setBudgetCost] = useState("");
  const [budgetHours, setBudgetHours] = useState("");
  const [budgetNotes, setBudgetNotes] = useState("");

  // Export form state
  const [exportForm, setExportForm] = useState({
    startDate: "",
    endDate: "",
    statusId: "",
    urgency: "",
    includeMilestones: true,
    includeAssignments: true,
    includeBudget: canViewProjectBudgets,
    groupBy: "", // empty = no grouping, "company" = client company, "contact" = client name
  });

  // Client dropdown state
  const [allClientCompanies, setAllClientCompanies] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showNameDropdown, setShowNameDropdown] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState("overview");

  const [milestones, setMilestones] = useState([]);

  const [milestoneFormData, setMilestoneFormData] = useState({
    milestone_name: "",
    description: "",
    start_date: "",
    due_date: "",
    status: "open",
  });

  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Search, sort, filter, group state
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [filterUrgency, setFilterUrgency] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterLabel, setFilterLabel] = useState("");
  const [filterEmployee, setFilterEmployee] = useState("");
  const [groupBy, setGroupBy] = useState("status");
  const [showArchive, setShowArchive] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem('projectsViewMode') || 'table';
    } catch {
      return 'table';
    }
  });
  useEffect(() => {
    try { localStorage.setItem('projectsViewMode', viewMode); } catch {}
  }, [viewMode]);
  const [showAddStatusModal, setShowAddStatusModal] = useState(false);
  const [addStatusContext, setAddStatusContext] = useState(null); // 'table', 'create', 'edit'

  const [columns, setColumns] = useState([
    { key: "client_company", label: "Company", visible: true },
    { key: "project_name", label: "Project", visible: true },
    { key: "client_name", label: "Client", visible: false },
    { key: "labels", label: "Labels", visible: true },
    { key: "email", label: "Email", visible: false },
    { key: "phone_number", label: "Phone", visible: false },
    { key: "start_date", label: "Start Date", visible: true },
    { key: "end_date", label: "End Date", visible: true },
    { key: "assignees", label: "Team", visible: true },
    { key: "urgency", label: "Urgency", visible: true },
    { key: "location", label: "Location", visible: false },
    { key: "address", label: "Address", visible: false },
    { key: "status", label: "Status", visible: true },
  ]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const toggleColumn = (key) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.key === key ? { ...col, visible: !col.visible } : col
      )
    );
  };

  const toggleRowExpand = (projectId) => {
    setExpandedRows(prev => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  // Reusable function to refresh all projects data
  const refreshProjects = async () => {
    try {
      const [statusesData, projectsData, archivedData] = await Promise.all([
        getProjectStatuses(),
        getProjects(),
        getProjects({ archived: true })
      ]);
      
      setStatuses(statusesData.statuses);
      
      const addStatusColor = (p) => {
        const status = statusesData.statuses.find(s => s.status_id === p.status_id);
        return { ...p, status_color: status?.color };
      };
      setProjects(projectsData.projects.map(addStatusColor));
      setArchivedProjects(archivedData.projects.map(addStatusColor));
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to refresh projects", "error");
    }
  };

  const applyStatusColors = (items, statusList) => {
    return items.map((project) => {
      const projectStatus = statusList.find((item) => item.status_id === project.status_id);
      return { ...project, status_color: projectStatus?.color };
    });
  };

  const handleSaveStatusOrder = async (orderedStatusIds) => {
    try {
      setSavingStatusOrder(true);
      const response = await reorderProjectStatuses(orderedStatusIds);
      const nextStatuses = response.statuses || [];

      setStatuses(nextStatuses);
      setProjects((prev) => applyStatusColors(prev, nextStatuses));
      setArchivedProjects((prev) => applyStatusColors(prev, nextStatuses));
      setShowStatusReorderModal(false);
      showToast('Project status order updated', 'success');
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Failed to reorder project statuses';
      showToast(msg, 'error');
    } finally {
      setSavingStatusOrder(false);
    }
  };

  // This effect intentionally runs against route/project state only to bootstrap modal opening once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
  const fetchData = async () => {
    try {
      const [statusesRes, projectsData, archivedData, employeesData, companiesData] = await Promise.all([
        getProjectStatuses(),
        getProjects(),
        getProjects({ archived: true }),
        getEmployees(),
        getAllClientCompanies()
      ]);
      
      setStatuses(statusesRes.statuses);

      const addStatusColor = (p) => {
        const status = statusesRes.statuses.find(s => s.status_id === p.status_id);
        return { ...p, status_color: status?.color };
      };
      setProjects(projectsData.projects.map(addStatusColor));
      setArchivedProjects(archivedData.projects.map(addStatusColor));

      setAvailableEmployees((employeesData.employees || employeesData || []).map(user => ({
        id: user.user_id,
        name: user.name,
        job_title: user.job_title
      })));

      setAllClientCompanies(companiesData);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to load projects", "error");
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, []);

  // Track if we've already handled the navigation state
  const [handledNavState, setHandledNavState] = useState(false);

  // Auto-open new project modal if navigated from dashboard
  useEffect(() => {
    // Skip if we've already handled the navigation state
    if (handledNavState) return;

    if (routeLocation.state?.openNewProjectModal) {
      setShowForm(true);
      setHandledNavState(true);
      // Clear the state so it doesn't reopen on subsequent renders
      window.history.replaceState({}, document.title);
    } else if (routeLocation.state?.openProjectDetailModal && projects.length > 0) {
      const projectId = routeLocation.state.openProjectDetailModal;
      const project = projects.find(p => p.project_id === projectId);
      if (project) {
        openProjectDetail(project);
        setHandledNavState(true);
        // Clear the state so it doesn't reopen on subsequent renders
        window.history.replaceState({}, document.title);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLocation, projects, handledNavState]);

  useEffect(() => {
    if (statuses.length && status === null) {
      const defaultStatus = statuses.find(s => s.name === "In Queue") || statuses[0];
      setStatus(defaultStatus.status_id);
    }
  }, [statuses, status]);

  useEffect(() => {
    if (statuses.length) {
      const groupState = {};
      statuses.forEach((s) => {
        groupState[s.name] = false;
      });
      setCollapsedGroups(groupState);
    }
  }, [statuses]);

  const toggleGroupCollapse = (status) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnKey);
      setSortDirection("asc");
    }
  };

  // Compute filtered and sorted project list
  const getDisplayedProjects = () => {
    let result = showArchive ? [...archivedProjects] : [...projects];

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (p) =>
          (p.project_name || "").toLowerCase().includes(term) ||
          (p.client_company || "").toLowerCase().includes(term) ||
          (p.client_name || "").toLowerCase().includes(term)
      );
    }

    // Urgency filter
    if (filterUrgency) {
      result = result.filter((p) => p.urgency === filterUrgency);
    }

    // Status filter
    if (filterStatus) {
      result = result.filter((p) => String(p.status_id) === filterStatus);
    }

    // Label filter
    if (filterLabel) {
      result = result.filter((p) =>
        p.labels && p.labels.some((l) => String(l.label_id) === filterLabel)
      );
    }

    // Employee filter
    if (filterEmployee) {
      result = result.filter((p) =>
        p.assignees && p.assignees.some((a) => String(a.employee_id) === filterEmployee)
      );
    }

    // Sorting
    if (sortColumn) {
      result = [...result].sort((a, b) => {
        let valA = a[sortColumn];
        let valB = b[sortColumn];

        // Handle date columns
        if (sortColumn === "start_date" || sortColumn === "end_date") {
          valA = valA ? new Date(valA).getTime() : 0;
          valB = valB ? new Date(valB).getTime() : 0;
        } else if (sortColumn === "assignees") {
          valA = a.assignee_count || 0;
          valB = b.assignee_count || 0;
        } else if (sortColumn === "urgency") {
          const order = { High: 3, Medium: 2, Low: 1 };
          valA = order[valA] || 0;
          valB = order[valB] || 0;
        } else if (sortColumn === "status") {
          const statusA = statuses.find((s) => s.status_id === a.status_id);
          const statusB = statuses.find((s) => s.status_id === b.status_id);
          valA = (statusA?.name || "").toLowerCase();
          valB = (statusB?.name || "").toLowerCase();
        } else {
          valA = (valA || "").toString().toLowerCase();
          valB = (valB || "").toString().toLowerCase();
        }

        if (valA < valB) return sortDirection === "asc" ? -1 : 1;
        if (valA > valB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  };

  const displayedProjects = getDisplayedProjects();

  const archivedCount = archivedProjects.length;

  const availableLabels = useMemo(() => {
    const labelMap = new Map();
    const source = showArchive ? archivedProjects : projects;
    source.forEach((p) => {
      (p.labels || []).forEach((l) => {
        if (!labelMap.has(l.label_id)) labelMap.set(l.label_id, l.name);
      });
    });
    return Array.from(labelMap.entries());
  }, [projects, archivedProjects, showArchive]);

  const hasActiveFilters = !!(searchTerm || filterUrgency || filterStatus || filterLabel || filterEmployee);

  const resetView = () => {
    setSearchTerm("");
    setFilterUrgency("");
    setFilterStatus("");
    setFilterLabel("");
    setFilterEmployee("");
    setSortColumn(null);
    setSortDirection("asc");
    setGroupBy("status");
  };

  const getGroupByValue = () => {
    if (!groupBy) return null;
    if (groupBy === "status") {
      return (project) => {
        const match = statuses.find(s => s.status_id === project.status_id);
        return match?.name?.trim() || "Unknown";
      };
    }
    return groupBy;
  };

  const handleMilestoneAssignmentSave = async () => {
    if (!editableMilestone?.milestone_id) {
      showToast("Invalid milestone", "error");
      return;
    }

    // Validate dates: start date should be same or earlier than due date
    if (editableMilestone.start_date && editableMilestone.due_date) {
      const startDate = new Date(editableMilestone.start_date);
      const dueDate = new Date(editableMilestone.due_date);
      if (startDate > dueDate) {
        showToast("Start date cannot be later than due date", "error");
        return;
      }
    }

    try {
      await updateMilestone(editableMilestone.milestone_id, {
        milestone_name: editableMilestone.milestone_name,
        description: editableMilestone.description,
        start_date: editableMilestone.start_date || null,
        due_date: editableMilestone.due_date || null,
        status: editableMilestone.status,
      });

      const teamPayload = {
        assignedBy: currentUser.user_id,
        employees: (editableMilestone.team_members || []).map(emp => ({
          employee_id: emp.id,
          role: emp.role || "",
        }))
      };

      await assignEmployeesToMilestone(editableMilestone.milestone_id, teamPayload);

      // Refresh all projects to get updated milestone data
      await refreshProjects();

      // Also update local milestones state for the modal
      if (editableProject) {
        const updatedMilestones = await getMilestones(editableProject.project_id);
        setMilestones(Array.isArray(updatedMilestones) ? updatedMilestones : []);
      }

      setShowMilestoneEditModal(false);
      showToast("Milestone saved successfully", "success");
    } catch (err) {
      console.error("Failed to save milestone:", err);
      showToast(err.message || "Failed to save milestone", "error");
    }
  };

  const handleEditMilestone = async (milestone) => {
    try {
      const team = await getMilestoneAssignments(milestone.milestone_id);
      const teamMembers = team.map((a) => ({
        id: a.employee_id,
        name: a.user_name,
        job_title: a.job_title,
        role: a.role_name,
        role_id: a.role_id,
      }));

      setEditableMilestone({
        ...milestone,
        team_members: teamMembers,
      });

      setShowMilestoneEditModal(true);
    } catch (err) {
      console.error("Failed to load milestone team:", err);
      showToast(err.message || "Failed to load milestone team", "error");

      setEditableMilestone({
        ...milestone,
        team_members: [],
      });

      setShowMilestoneEditModal(true);
    }
  };


  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowSettings(false);
      }
    };

    if (showSettings) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSettings]);

  const clearForm = () => {
    setProjectName("");
    setClientName("");
    setClientCompany("");
    setSelectedLabels([]);
    setEmail("");
    setPhoneNumber("");
    setAssignedEmployees([]);
    setStartDate("");
    setEndDate("");
    setUrgency("Medium");
    setDescription("");
    setStatus(statuses.find(s => s.name === "In Queue")?.status_id || statuses[0]?.status_id || null);
    setProjectColor(getRandomProjectColor());
    setBudgetCost("");
    setBudgetHours("");
    setBudgetNotes("");
    setProjectLocation("");
    setAddress("");
    setActiveTab("overview");
    setError(null);
    // Clear dropdown state
    setCompanySuggestions([]);
    setNameSuggestions([]);
    setClientContacts([]);
    setShowCompanyDropdown(false);
    setShowNameDropdown(false);
  };

  const handleCompanyFocus = () => {
    setShowCompanyDropdown(true);
    setCompanySuggestions(allClientCompanies.map(c => c.company_name));
  };

  const handleCompanyInput = (val) => {
    setClientCompany(val);
    
    if (val.trim() === '') {
      setCompanySuggestions(allClientCompanies.map(c => c.company_name));
    } else {
      // Check if there's an exact match
      const exactMatch = allClientCompanies.find(c => 
        c.company_name.toLowerCase() === val.toLowerCase()
      );
      
      if (exactMatch) {
        // If exact match exists, only show that
        setCompanySuggestions([exactMatch.company_name]);
      } else {
        // Otherwise, show all that start with the query
        const filtered = allClientCompanies
          .filter(c => c.company_name.toLowerCase().startsWith(val.toLowerCase()))
          .map(c => c.company_name);
        setCompanySuggestions(filtered);
      }
    }
  };

  const handleCompanySelect = (companyName) => {
    setClientCompany(companyName);
    setCompanySuggestions([]);
    setShowCompanyDropdown(false);
    // When company is selected, load contacts for that company
    loadContactsForCompany(companyName);
  };

  const loadContactsForCompany = async (companyName) => {
    if (!companyName) {
      setClientContacts([]);
      return;
    }
    const company = allClientCompanies.find(c => c.company_name === companyName);
    if (company) {
      const contacts = await getClientContacts(company.client_company_id);
      setClientContacts(contacts);
    }
  };

  const handleNameFocus = async () => {
    setShowNameDropdown(true);
    if (clientCompany) {
      // Already have contacts loaded for this company
      updateNameSuggestions(clientName);
    } else {
      // Load all contacts when no company is selected
      if (!clientContacts.length) {
        const allContacts = await getClientContacts(null);
        setClientContacts(allContacts);
      }
      updateNameSuggestions(clientName);
    }
  };

  const handleNameInput = (val) => {
    setClientName(val);
    updateNameSuggestions(val);
  };

  const updateNameSuggestions = (query) => {
    let suggestions = [];
    if (clientCompany && clientContacts.length > 0) {
      // Filter contacts for selected company
      suggestions = clientContacts
        .filter(contact => contact.name && contact.name.trim())
        .filter(contact => 
          !query || contact.name.toLowerCase().startsWith(query.toLowerCase())
        )
        .map(contact => contact.name);
    } else if (!clientCompany && clientContacts.length > 0) {
      // No company selected, show all contacts
      suggestions = clientContacts
        .filter(contact => contact.name && contact.name.trim())
        .filter(contact => 
          !query || contact.name.toLowerCase().startsWith(query.toLowerCase())
        )
        .map(contact => contact.name);
    }
    setNameSuggestions(suggestions);
  };

  const handleNameSelect = (contactName) => {
    setClientName(contactName);
    setNameSuggestions([]);
    setShowNameDropdown(false);
    
    // Autofill email and phone if contact exists
    const contact = clientContacts.find(c => c.name === contactName);
    if (contact) {
      setEmail(contact.email || '');
      setPhoneNumber(contact.phone_number || '');
    }
  };

  const handleEditCompanyFocus = () => {
    setShowCompanyDropdown(true);
    setCompanySuggestions(allClientCompanies.map(c => c.company_name));
  };

  const handleEditCompanyInput = (val) => {
    setEditableProject({ ...editableProject, client_company: val });
    
    if (val.trim() === '') {
      setCompanySuggestions(allClientCompanies.map(c => c.company_name));
    } else {
      // Check if there's an exact match
      const exactMatch = allClientCompanies.find(c => 
        c.company_name.toLowerCase() === val.toLowerCase()
      );
      
      if (exactMatch) {
        // If exact match exists, only show that
        setCompanySuggestions([exactMatch.company_name]);
      } else {
        // Otherwise, show all that start with the query
        const filtered = allClientCompanies
          .filter(c => c.company_name.toLowerCase().startsWith(val.toLowerCase()))
          .map(c => c.company_name);
        setCompanySuggestions(filtered);
      }
    }
  };

  const handleEditCompanySelect = (companyName) => {
    setEditableProject({ ...editableProject, client_company: companyName });
    setCompanySuggestions([]);
    setShowCompanyDropdown(false);
    // When company is selected, load contacts for that company
    loadContactsForCompany(companyName);
  };

  const handleEditNameFocus = async () => {
    setShowNameDropdown(true);
    const currentCompany = editableProject?.client_company;
    if (currentCompany) {
      // Check if we have contacts loaded for this company
      const company = allClientCompanies.find(c => c.company_name === currentCompany);
      if (company && (!clientContacts.length || clientContacts[0]?.company_name !== currentCompany)) {
        // Load contacts for this company if not already loaded
        const contacts = await getClientContacts(company.client_company_id);
        setClientContacts(contacts);
      }
      updateEditNameSuggestions(editableProject?.client_name || '');
    } else {
      // Load all contacts when no company is selected
      if (!clientContacts.length) {
        const allContacts = await getClientContacts(null);
        setClientContacts(allContacts);
      }
      updateEditNameSuggestions(editableProject?.client_name || '');
    }
  };

  const handleEditNameInput = (val) => {
    setEditableProject({ ...editableProject, client_name: val });
    updateEditNameSuggestions(val);
  };

  const updateEditNameSuggestions = (query) => {
    let suggestions = [];
    if (editableProject?.client_company && clientContacts.length > 0) {
      // Filter contacts for selected company
      suggestions = clientContacts
        .filter(contact => contact.name && contact.name.trim())
        .filter(contact => 
          !query || contact.name.toLowerCase().startsWith(query.toLowerCase())
        )
        .map(contact => contact.name);
    } else if (!editableProject?.client_company && clientContacts.length > 0) {
      // No company selected, show all contacts
      suggestions = clientContacts
        .filter(contact => contact.name && contact.name.trim())
        .filter(contact => 
          !query || contact.name.toLowerCase().startsWith(query.toLowerCase())
        )
        .map(contact => contact.name);
    }
    setNameSuggestions(suggestions);
  };

  const handleEditNameSelect = (contactName) => {
    const contact = clientContacts.find(c => c.name === contactName);
    setEditableProject({ 
      ...editableProject, 
      client_name: contactName,
      email: contact ? contact.email || '' : editableProject.email,
      phone_number: contact ? contact.phone_number || '' : editableProject.phone_number
    });
    setNameSuggestions([]);
    setShowNameDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate dates: start date should be same or earlier than end date
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (start > end) {
        showToast("Start date cannot be later than end date", "error");
        return;
      }
    }

    try {
      const result = await createProject({
        client_name: clientName,
        client_company: clientCompany,
        project_name: projectName,
        email,
        phone_number: phoneNumber,
        start_date: startDate,
        end_date: endDate,
        urgency,
        description,
        status_id: status,
        color: projectColor,
        budget_cost: budgetCost ? parseFloat(budgetCost) : null,
        budget_hours: budgetHours ? parseFloat(budgetHours) : null,
        location: projectLocation,
        address,
      });

      // Save labels for the new project
      if (result.project && selectedLabels.length > 0) {
        await setProjectLabels(
          result.project.project_id,
          selectedLabels.map(l => l.label_id)
        );
      }

      setShowForm(false);
      clearForm();
      setError(null);

      await refreshProjects();
      showToast("Project created successfully", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to save project", "error");
    }
  };

  const handleExportSubmit = async (e) => {
    e.preventDefault();
    try {
      // Build query params for export
      const params = {};

      if (exportForm.startDate) params.start_date = exportForm.startDate;
      if (exportForm.endDate) params.end_date = exportForm.endDate;
      if (exportForm.statusId) params.status_id = exportForm.statusId;
      if (exportForm.urgency) params.urgency = exportForm.urgency;
      if (exportForm.includeMilestones) params.include_milestones = 'true';
      if (exportForm.includeAssignments) params.include_assignments = 'true';
      if (exportForm.includeBudget && canViewProjectBudgets) params.include_budget = 'true';
      if (exportForm.groupBy) params.group_by = exportForm.groupBy;

      // Call the export service
      await exportProjects(params);
      showToast('Projects exported successfully', 'success');
      setShowExportModal(false);
    } catch (error) {
      console.error("Export failed:", error);
      showToast(error.message || "Failed to export projects", "error");
    }
  };

  const doUpdateProject = async () => {
    // Validate dates: start date should be same or earlier than end date
    if (editableProject.start_date && editableProject.end_date) {
      const start = new Date(editableProject.start_date);
      const end = new Date(editableProject.end_date);
      if (start > end) {
        showToast("Start date cannot be later than end date", "error");
        return;
      }
    }

    try {
      // Only update basic project info if user can edit projects
      if (canEditProjects) {
        await updateProject(editableProject.project_id, {
          ...editableProject,
        });
        
        // Save labels
        await setProjectLabels(
          editableProject.project_id,
          editLabels.map(l => l.label_id)
        );
      }

      // Only update budget if user has permission
      if (canManageProjectBudgets) {
        await updateProjectBudget(editableProject.project_id, {
          budget_cost: budgetCost ? parseFloat(budgetCost) : 0,
          budget_hours: budgetHours ? parseFloat(budgetHours) : 0,
          notes: budgetNotes,
        });
      }

      // Only update team if user has permission
      if (canManageProjectTeam) {
        await assignEmployeesToProject(editableProject.project_id, {
          projectId: editableProject.project_id,
          clientId: editableProject.client_id || null,
          assignedBy: currentUser.user_id,
          employees: assignedEmployees.map((emp) => ({
            user_id: emp.id,
            role: emp.role,
          })),
        });
      }

      // Only update milestones if user can edit projects
      if (canEditProjects) {
        await syncMilestones(editableProject.project_id, milestones);
      }

      // Refresh all projects to get updated data including assignees
      await refreshProjects();

      setShowEditModal(false);
      setEditableProject(null);
      setEditLabels([]);
      setActiveTab("overview");
      showToast("Project updated successfully", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update project", "error");
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();

    // Check if status is being changed to "completed"
    const newStatus = statuses.find(s => s.status_id === editableProject.status_id);
    const originalProject = projects.find(p => p.project_id === editableProject.project_id);
    const originalStatus = statuses.find(s => s.status_id === originalProject?.status_id);
    const isChangingToCompleted = newStatus?.name?.toLowerCase() === 'completed'
      && originalStatus?.name?.toLowerCase() !== 'completed';

    if (isChangingToCompleted) {
      setPendingArchiveAction(() => async () => {
        await doUpdateProject();
      });
      setShowArchiveConfirm(true);
      return;
    }

    await doUpdateProject();
  };

  const handleStatusChange = async (project, newStatusId) => {
    const newStatus = statuses.find(s => s.status_id === newStatusId);
    if (newStatus && newStatus.name?.toLowerCase() === 'completed') {
      setPendingArchiveAction(() => async () => {
        try {
          await updateProject(project.project_id, { status_id: newStatusId });
          await refreshProjects();
          showToast("Status updated successfully", "success");
        } catch (err) {
          console.error(err);
          showToast(err.message || "Failed to update status", "error");
        }
      });
      setShowArchiveConfirm(true);
      return;
    }
    try {
      await updateProject(project.project_id, { status_id: newStatusId });
      await refreshProjects();
      showToast("Status updated successfully", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update status", "error");
    }
  };

  const handleCreateStatus = async ({ name, color, description }) => {
    const result = await createProjectStatus({ name, color, description });
    const newStatus = result.status;
    // Refresh statuses list
    const statusesRes = await getProjectStatuses();
    setStatuses(statusesRes.statuses);
    // Auto-select the new status in the relevant context
    if (addStatusContext === 'create') {
      setStatus(newStatus.status_id);
    } else if (addStatusContext === 'edit' && editableProject) {
      setEditableProject({ ...editableProject, status_id: newStatus.status_id });
    }
    setShowAddStatusModal(false);
    setAddStatusContext(null);
    showToast(`Status "${newStatus.name}" created`, "success");
  };

  const handleRestoreProject = async (project) => {
    try {
      const firstStatus = statuses.find(s => s.name?.toLowerCase() !== "completed");
      if (!firstStatus) {
        showToast("No active status available to restore to", "error");
        return;
      }
      await updateProject(project.project_id, { status_id: firstStatus.status_id });
      await refreshProjects();
      showToast("Project restored successfully", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to restore project", "error");
    }
  };

  const handleUrgencyChange = async (project, newUrgency) => {
    try {
      await updateProject(project.project_id, { urgency: newUrgency });
      await refreshProjects();
      showToast("Urgency updated successfully", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update urgency", "error");
    }
  };

  const openProjectDetail = async (project) => {
    try {
      setEditableProject(project);
      
      // Load contacts for the existing company
      if (project.client_company) {
        await loadContactsForCompany(project.client_company);
      }
      
      const assignments = await getProjectAssignments(project.project_id);
      const employeeList = Array.isArray(assignments) ? assignments : (assignments.assignments || []);
      
      const mappedEmployees = employeeList.map(a => 
      ({
        id: a.employee_id,
        name: a.user_name,
        role: a.role_name,
        job_title: a.job_title,
      }));
      
      setAssignedEmployees(mappedEmployees);

      // Fetch labels for this project
      try {
        const labelData = await getProjectLabels(project.project_id);
        setEditLabels(labelData.labels || []);
      } catch (err) {
        console.error("Failed to fetch project labels:", err);
        setEditLabels([]);
      }

      const milestonesData = await getMilestones(project.project_id);
      setMilestones(Array.isArray(milestonesData) ? milestonesData : []);

      if (canViewProjectBudgets) {
        try {
          const budgetData = await getProjectBudget(project.project_id);
          setBudgetCost(budgetData.budget_cost || "");
          setBudgetHours(budgetData.budget_hours || "");
          setBudgetNotes(budgetData.notes || "");
          
          setBudgetTracking({
            scheduled_hours: budgetData.scheduled_hours || 0,
            scheduled_cost: budgetData.scheduled_cost || 0,
            actual_hours: budgetData.actual_hours || 0,
            actual_cost: budgetData.actual_cost || 0,
            scheduled_breakdown: budgetData.scheduled_breakdown || [],
            actual_breakdown: budgetData.actual_breakdown || []
          });
        } catch (err) {
          console.warn("Could not fetch budget (user may not have permission):", err);
          setBudgetCost("");
          setBudgetHours("");
          setBudgetNotes("");
          setBudgetTracking({
            scheduled_hours: 0,
            scheduled_cost: 0,
            actual_hours: 0,
            actual_cost: 0,
            scheduled_breakdown: [],
            actual_breakdown: []
          });
        }
      } else {
        setBudgetCost("");
        setBudgetHours("");
        setBudgetNotes("");
        setBudgetTracking({
          scheduled_hours: 0,
          scheduled_cost: 0,
          actual_hours: 0,
          actual_cost: 0,
          scheduled_breakdown: [],
          actual_breakdown: []
        });
      }

      setShowDetailModal(true);
    } catch (err) {
      console.error("Failed to load project details:", err);
      showToast(err.message || "Failed to load project details", "error");
    }
  };

  const overviewFields = [
    { 
      section: "overview", 
      label: "Project Name", 
      type: "custom",
      render: () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <input
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            style={{ flex: 1 }}
          />
          <ColorPicker
            value={projectColor}
            onChange={(e) => setProjectColor(e.target.value)}
          />
        </div>
      )
    },
    { 
      section: "overview", 
      label: "Client Details", 
      type: "custom",
      render: () => (
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Client Name</label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => handleNameInput(e.target.value)}
              onFocus={handleNameFocus}
              onBlur={() => setTimeout(() => setShowNameDropdown(false), 200)}
              style={{ width: '100%' }}
            />
            {nameSuggestions.length > 0 && showNameDropdown && (
              <ul style={{ position: 'absolute', zIndex: 10, background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', listStyle: 'none', padding: '4px 0', margin: '4px 0 0', width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                {nameSuggestions.map((s, i) => (
                  <li key={i} onClick={() => handleNameSelect(s)} style={{ padding: '8px 12px', cursor: 'pointer' }}>{s}</li>
                ))}
              </ul>
            )}
          </div>
          <div style={{ flex: 1, position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Company Name</label>
            <input
              type="text"
              value={clientCompany}
              onChange={(e) => handleCompanyInput(e.target.value)}
              onFocus={handleCompanyFocus}
              onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 200)}
              style={{ width: '100%' }}
            />
            {companySuggestions.length > 0 && showCompanyDropdown && (
              <ul style={{ position: 'absolute', zIndex: 10, background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', listStyle: 'none', padding: '4px 0', margin: '4px 0 0', width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                {companySuggestions.map((s, i) => (
                  <li key={i} onClick={() => handleCompanySelect(s)} style={{ padding: '8px 12px', cursor: 'pointer' }}>{s}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )
    },
    { section: "overview", label: "Email", value: email, onChange: (e) => setEmail(e.target.value), type: "email" },
    { section: "overview", label: "Phone Number", value: phoneNumber, onChange: (e) => setPhoneNumber(e.target.value), type: "tel" },
    { 
      section: "overview", 
      label: "Labels", 
      type: "custom",
      render: () => (
        <LabelSelector
          selectedLabels={selectedLabels}
          onChange={setSelectedLabels}
          placeholder="Add service type labels..."
        />
      )
    },
    { 
      section: "overview", 
      label: "Project Dates", 
      type: "custom",
      render: () => (
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      )
    },
    { 
      section: "overview", 
      label: "Project Status", 
      type: "custom",
      render: () => (
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Urgency</label>
            <CustomDropdown
              options={[
                { value: 'Low', label: 'Low' },
                { value: 'Medium', label: 'Medium' },
                { value: 'High', label: 'High' },
              ]}
              value={urgency}
              onChange={(val) => setUrgency(val)}
              fullWidth
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Status</label>
            <StatusDropdown
              statuses={statuses}
              value={status || null}
              onChange={(id) => setStatus(id)}
              onAddNew={() => { setAddStatusContext('create'); setShowAddStatusModal(true); }}
              fullWidth
            />
          </div>
        </div>
      )
    },
    { 
      section: "overview", 
      label: "Location & Address", 
      type: "custom",
      render: () => (
        <div style={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Location</label>
            <input
              type="text"
              value={projectLocation}
              onChange={(e) => setProjectLocation(e.target.value)}
              placeholder="e.g., New York, NY"
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Address</label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Full street address"
              style={{ width: '100%', minHeight: '60px' }}
            />
          </div>
        </div>
      )
    },
    { section: "overview", label: "Description", value: description, onChange: (e) => setDescription(e.target.value), type: "textarea" },
  ];

  const budgetFields = [
    {
      label: "Estimated Budget ($)",
      value: budgetCost,
      onChange: (e) => setBudgetCost(e.target.value),
      type: "number",
      step: "0.01",
      min: "0",
      placeholder: "0.00"
    },
    {
      label: "Estimated Hours",
      value: budgetHours,
      onChange: (e) => setBudgetHours(e.target.value),
      type: "number",
      step: "0.5",
      min: "0",
      placeholder: "0"
    },
    {
      label: "Budget Notes",
      value: budgetNotes,
      onChange: (e) => setBudgetNotes(e.target.value),
      type: "textarea",
      placeholder: "Additional notes about the budget..."
    }
  ];

  const [budgetTracking, setBudgetTracking] = useState({
    scheduled_hours: 0,
    scheduled_cost: 0,
    actual_hours: 0,
    actual_cost: 0,
    scheduled_breakdown: [],
    actual_breakdown: []
  });

  const [showBudgetEditModal, setShowBudgetEditModal] = useState(false);
  const [editableBudget, setEditableBudget] = useState({
    budget_cost: "",
    budget_hours: "",
    notes: ""
  });

  const renderBudgetSummary = () => {
    const estimatedBudget = budgetCost ? parseFloat(budgetCost) : 0;
    const estimatedHours = budgetHours ? parseFloat(budgetHours) : 0;
    
    // Calculate totals (using mock data for now)
    const scheduledHours = budgetTracking.scheduled_hours;
    const scheduledCost = budgetTracking.scheduled_cost;
    const actualHours = budgetTracking.actual_hours;
    const actualCost = budgetTracking.actual_cost;

    if (!budgetCost && !budgetHours && !budgetNotes) {
      return (
        <div className={ui.inputGroup}>
          <button 
            type="button"
            className={ui.saveButton}
            onClick={() => {
              setEditableBudget({
                budget_cost: budgetCost || "",
                budget_hours: budgetHours || "",
                notes: budgetNotes || ""
              });
              setShowBudgetEditModal(true);
            }}
          >
            Add Budget
          </button>
          <p className={ui.milestoneEmptyText}>
            No budget information available
          </p>
        </div>
      );
    }

    return (
      <div className={ui.budgetSummaryContainer}>
        {/* Budget Overview Cards */}
        <div className={ui.budgetOverviewGrid}>
          {/* Cost Overview */}
          <div className={ui.budgetCard}>
            <h4 className={ui.budgetCardTitle}>Cost Overview</h4>
            <div className={ui.budgetMetrics}>
              <div className={ui.budgetMetric}>
                <label>Estimated</label>
                <div className={ui.metricValue}>
                  ${estimatedBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className={ui.budgetMetric}>
                <label>Scheduled</label>
                <div className={ui.metricValue}>
                  ${scheduledCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className={ui.metricSubtext}>
                  {estimatedBudget > 0 ? ((scheduledCost / estimatedBudget) * 100).toFixed(1) : 0}% of budget
                </div>
              </div>
              <div className={ui.budgetMetric}>
                <label>Scheduled Remaining</label>
                <div className={`${ui.metricValue} ${(estimatedBudget - scheduledCost) < 0 ? ui.metricNegative : ui.metricPositive}`}>
                  ${Math.abs(estimatedBudget - scheduledCost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className={`${ui.metricSubtext} ${(estimatedBudget - scheduledCost) < 0 ? ui.metricNegative : ui.metricPositive}`}>
                  {(estimatedBudget - scheduledCost) < 0 ? 'Over' : 'Under'} by {estimatedBudget > 0 ? Math.abs(((estimatedBudget - scheduledCost) / estimatedBudget) * 100).toFixed(1) : 0}%
                </div>
              </div>
              <div className={ui.budgetMetric}>
                <label>Actual</label>
                <div className={ui.metricValue}>
                  ${actualCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className={ui.metricSubtext}>
                  {estimatedBudget > 0 ? ((actualCost / estimatedBudget) * 100).toFixed(1) : 0}% of budget
                </div>
              </div>
              <div className={ui.budgetMetric}>
                <label>Actual Remaining</label>
                <div className={`${ui.metricValue} ${(estimatedBudget - actualCost) < 0 ? ui.metricNegative : ui.metricPositive}`}>
                  ${Math.abs(estimatedBudget - actualCost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className={`${ui.metricSubtext} ${(estimatedBudget - actualCost) < 0 ? ui.metricNegative : ui.metricPositive}`}>
                  {(estimatedBudget - actualCost) < 0 ? 'Over' : 'Under'} by {estimatedBudget > 0 ? Math.abs(((estimatedBudget - actualCost) / estimatedBudget) * 100).toFixed(1) : 0}%
                </div>
              </div>
            </div>
          </div>

          {/* Hours Overview */}
          <div className={ui.budgetCard}>
            <h4 className={ui.budgetCardTitle}>Hours Overview</h4>
            <div className={ui.budgetMetrics}>
              <div className={ui.budgetMetric}>
                <label>Estimated</label>
                <div className={ui.metricValue}>
                  {estimatedHours.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} hrs
                </div>
              </div>
              <div className={ui.budgetMetric}>
                <label>Scheduled</label>
                <div className={ui.metricValue}>
                  {scheduledHours.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} hrs
                </div>
                <div className={ui.metricSubtext}>
                  {estimatedHours > 0 ? ((scheduledHours / estimatedHours) * 100).toFixed(1) : 0}% allocated
                </div>
              </div>
              <div className={ui.budgetMetric}>
                <label>Scheduled Remaining</label>
                <div className={`${ui.metricValue} ${(estimatedHours - scheduledHours) < 0 ? ui.metricNegative : ui.metricPositive}`}>
                  {Math.abs(estimatedHours - scheduledHours).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} hrs
                </div>
                <div className={`${ui.metricSubtext} ${(estimatedHours - scheduledHours) < 0 ? ui.metricNegative : ui.metricPositive}`}>
                  {(estimatedHours - scheduledHours) < 0 ? 'Over' : 'Under'} by {estimatedHours > 0 ? Math.abs(((estimatedHours - scheduledHours) / estimatedHours) * 100).toFixed(1) : 0}%
                </div>
              </div>
              <div className={ui.budgetMetric}>
                <label>Actual</label>
                <div className={ui.metricValue}>
                  {actualHours.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} hrs
                </div>
                <div className={ui.metricSubtext}>
                  {estimatedHours > 0 ? ((actualHours / estimatedHours) * 100).toFixed(1) : 0}% used
                </div>
              </div>
              <div className={ui.budgetMetric}>
                <label>Actual Remaining</label>
                <div className={`${ui.metricValue} ${(estimatedHours - actualHours) < 0 ? ui.metricNegative : ui.metricPositive}`}>
                  {Math.abs(estimatedHours - actualHours).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} hrs
                </div>
                <div className={`${ui.metricSubtext} ${(estimatedHours - actualHours) < 0 ? ui.metricNegative : ui.metricPositive}`}>
                  {(estimatedHours - actualHours) < 0 ? 'Over' : 'Under'} by {estimatedHours > 0 ? Math.abs(((estimatedHours - actualHours) / estimatedHours) * 100).toFixed(1) : 0}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bars */}
        <div className={ui.budgetProgressSection}>
          {/* Budget Progress Bars */}
          <div className={ui.progressBarGroup}>
            <h5 className={ui.progressGroupTitle}>Budget Utilization</h5>
            
            {/* Scheduled Budget Bar */}
            <div className={ui.progressBar}>
              <div className={ui.progressBarHeader}>
                <label>Scheduled vs Estimated</label>
                <span>{estimatedBudget > 0 ? ((scheduledCost / estimatedBudget) * 100).toFixed(1) : 0}%</span>
              </div>
              <div className={ui.progressBarTrack}>
                <div 
                  className={`${ui.progressBarFill} ${ui.progressBarScheduledFill} ${scheduledCost > estimatedBudget ? ui.progressBarOverage : ''}`}
                  style={{ width: `${Math.min((scheduledCost / estimatedBudget) * 100, 100)}%` }}
                />
              </div>
              <div className={ui.progressBarLegend}>
                <span className={ui.legendItem}>
                  <span className={ui.legendColorScheduledFill}></span> Scheduled: ${scheduledCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {/* Actual Budget Bar */}
            <div className={ui.progressBar}>
              <div className={ui.progressBarHeader}>
                <label>Actual vs Estimated</label>
                <span>{estimatedBudget > 0 ? ((actualCost / estimatedBudget) * 100).toFixed(1) : 0}%</span>
              </div>
              <div className={ui.progressBarTrack}>
                <div 
                  className={`${ui.progressBarFill} ${actualCost > estimatedBudget ? ui.progressBarOverage : ''}`}
                  style={{ width: `${Math.min((actualCost / estimatedBudget) * 100, 100)}%` }}
                />
              </div>
              <div className={ui.progressBarLegend}>
                <span className={ui.legendItem}>
                  <span className={ui.legendColorActual}></span> Actual: ${actualCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          {/* Hours Progress Bars */}
          <div className={ui.progressBarGroup}>
            <h5 className={ui.progressGroupTitle}>Hours Utilization</h5>
            
            {/* Scheduled Hours Bar */}
            <div className={ui.progressBar}>
              <div className={ui.progressBarHeader}>
                <label>Scheduled vs Estimated</label>
                <span>{estimatedHours > 0 ? ((scheduledHours / estimatedHours) * 100).toFixed(1) : 0}%</span>
              </div>
              <div className={ui.progressBarTrack}>
                <div 
                  className={`${ui.progressBarFill} ${ui.progressBarScheduledFill} ${scheduledHours > estimatedHours ? ui.progressBarOverage : ''}`}
                  style={{ width: `${Math.min((scheduledHours / estimatedHours) * 100, 100)}%` }}
                />
              </div>
              <div className={ui.progressBarLegend}>
                <span className={ui.legendItem}>
                  <span className={ui.legendColorScheduledFill}></span> Scheduled: {scheduledHours.toLocaleString('en-US', { minimumFractionDigits: 1 })} hrs
                </span>
              </div>
            </div>

            {/* Actual Hours Bar */}
            <div className={ui.progressBar}>
              <div className={ui.progressBarHeader}>
                <label>Actual vs Estimated</label>
                <span>{estimatedHours > 0 ? ((actualHours / estimatedHours) * 100).toFixed(1) : 0}%</span>
              </div>
              <div className={ui.progressBarTrack}>
                <div 
                  className={`${ui.progressBarFill} ${actualHours > estimatedHours ? ui.progressBarOverage : ''}`}
                  style={{ width: `${Math.min((actualHours / estimatedHours) * 100, 100)}%` }}
                />
              </div>
              <div className={ui.progressBarLegend}>
                <span className={ui.legendItem}>
                  <span className={ui.legendColorActual}></span> Actual: {actualHours.toLocaleString('en-US', { minimumFractionDigits: 1 })} hrs
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Budget Line Items */}
        {budgetTracking.line_items && budgetTracking.line_items.length > 0 && (
          <div className={ui.budgetLineItemsSection}>
            <h4>Budget Breakdown</h4>
            <table className={ui.budgetLineItemsTable}>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Estimated</th>
                  <th>Actual</th>
                  <th>Variance</th>
                </tr>
              </thead>
              <tbody>
                {budgetTracking.line_items.map((item, idx) => {
                  const variance = item.estimated - item.actual;
                  const variancePercent = item.estimated > 0 ? ((variance / item.estimated) * 100).toFixed(1) : 0;
                  return (
                    <tr key={idx}>
                      <td>{item.category}</td>
                      <td>{item.description}</td>
                      <td>${item.estimated.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td>${item.actual.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td className={variance < 0 ? ui.metricNegative : ui.metricPositive}>
                        ${Math.abs(variance).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        <span className={ui.variancePercent}>({variance < 0 ? '+' : '-'}{Math.abs(variancePercent)}%)</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Budget Notes */}
        {budgetNotes && (
          <div className={ui.budgetNotesSection}>
            <h4>Budget Notes</h4>
            <div className={ui.budgetNotes}>{budgetNotes}</div>
          </div>
        )}

        {/* Edit Button */}
        <div className={ui.budgetActions}>
          <button 
            type="button"
            className={ui.buttonSecondary}
            onClick={() => {
              setEditableBudget({
                budget_cost: budgetCost || "",
                budget_hours: budgetHours || "",
                notes: budgetNotes || ""
              });
              setShowBudgetEditModal(true);
            }}
          >
            Edit Budget
          </button>
        </div>
      </div>
    );
  };

  const handleSaveBudget = async () => {
    try {
      await updateProjectBudget(editableProject.project_id, {
        budget_cost: editableBudget.budget_cost ? parseFloat(editableBudget.budget_cost) : 0,
        budget_hours: editableBudget.budget_hours ? parseFloat(editableBudget.budget_hours) : 0,
        notes: editableBudget.notes,
      });

      setBudgetCost(editableBudget.budget_cost);
      setBudgetHours(editableBudget.budget_hours);
      setBudgetNotes(editableBudget.notes);

      setShowBudgetEditModal(false);
      showToast("Budget updated successfully", "success");
    } catch (err) {
      console.error("Failed to update budget:", err);
      showToast(err.message || "Failed to update budget", "error");
    }
  };

  // Show access denied if no view permissions
  if (!canViewAllProjects && !canViewAssignedProjects) {
    return (
      <div className={ui.emptyState}>
        <p>You do not have permission to view projects.</p>
      </div>
    );
  }

  const isViewingAssignedOnly = !canViewAllProjects && canViewAssignedProjects;

  return (
    <div className={`${ui.pageContainer} ${viewMode === 'gantt' ? ui.pageContainerGantt : ''}`}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Page Header */}
      <div className={ui.pageHeader}>
        <div className={ui.pageHeaderLeft}>
          <h1 className={ui.pageTitle}>{showArchive ? 'Projects Archive' : 'Projects'}</h1>
          {!showLoading && statuses.length > 0 && (
            <span className={ui.pageCount}>
              {displayedProjects.length} {displayedProjects.length === 1 ? 'project' : 'projects'}
            </span>
          )}
        </div>
        <div className={ui.pageHeaderRight}>
          <div className={ui.viewToggleGroup} role="tablist" aria-label="View mode">
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'table'}
              className={`${ui.viewToggleButton} ${viewMode === 'table' ? ui.viewToggleButtonActive : ''}`}
              onClick={() => setViewMode('table')}
            >
              Table
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'gantt'}
              className={`${ui.viewToggleButton} ${viewMode === 'gantt' ? ui.viewToggleButtonActive : ''}`}
              onClick={() => setViewMode('gantt')}
            >
              Gantt
            </button>
          </div>
          {viewMode === 'table' && (
          <div style={{ position: "relative" }} ref={settingsRef}>
            <button
              className={ui.iconButton}
              onClick={() => setShowSettings((prev) => !prev)}
              title="Column Settings"
            >
              <img src={settingsIcon} alt="Settings" width="18" height="18" />
            </button>

            {showSettings && (
              <SettingsToggle
                columns={columns}
                toggleColumn={toggleColumn}
                refEl={settingsRef}
              />
            )}
          </div>
          )}
          
          <button
            className={`${ui.archiveToggle} ${showArchive ? ui.archiveToggleActive : ''}`}
            onClick={() => {
              setShowArchive((prev) => !prev);
              setFilterStatus("");
              setSearchTerm("");
            }}
            title={showArchive ? "View active projects" : "View archived projects"}
          >
            {showArchive ? '← Active' : 'Archive'}
            {!showArchive && archivedCount > 0 && (
              <span className={ui.archiveBadge}>{archivedCount}</span>
            )}
          </button>
          
          <button 
            className={ui.exportButton} 
            onClick={() => setShowExportModal(true)}
            title="Export to CSV"
          >
            Export
          </button>

          {canManageCompanySettings && statuses.length > 1 && (
            <button
              className={ui.exportButton}
              onClick={() => setShowStatusReorderModal(true)}
              title="Reorder statuses"
            >
              Reorder Statuses
            </button>
          )}
          
          {canCreateProjects && (
            <button className={ui.addButton} onClick={() => setShowForm(true)}>
              <img src={plusIcon} alt="" width="16" height="16" />
              Add Project
            </button>
          )}
        </div>
      </div>

      {/* Toolbar: Search, Filter, Group */}
      {!showLoading && (
        <div className={ui.tableToolbar}>
          <input
            type="text"
            className={ui.toolbarSearch}
            placeholder="Search projects…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <CustomDropdown
            options={[
              { value: '', label: 'All Urgencies' },
              { value: 'Low', label: 'Low' },
              { value: 'Medium', label: 'Medium' },
              { value: 'High', label: 'High' },
            ]}
            value={filterUrgency}
            onChange={(val) => setFilterUrgency(val)}
            toolbar
          />
          <CustomDropdown
            options={[
              { value: '', label: 'All Statuses' },
              ...statuses
                .filter((s) => showArchive
                  ? s.name?.toLowerCase() === "completed"
                  : s.name?.toLowerCase() !== "completed"
                )
                .map((s) => ({ value: String(s.status_id), label: s.name }))
            ]}
            value={filterStatus}
            onChange={(val) => setFilterStatus(val)}
            toolbar
          />
          <CustomDropdown
            options={[
              { value: '', label: 'All Labels' },
              ...availableLabels.map(([id, name]) => ({ value: String(id), label: name }))
            ]}
            value={filterLabel}
            onChange={(val) => setFilterLabel(val)}
            toolbar
          />
          <CustomDropdown
            options={[
              { value: '', label: 'All Employees' },
              ...availableEmployees.map((emp) => ({ value: String(emp.id), label: emp.name }))
            ]}
            value={filterEmployee}
            onChange={(val) => setFilterEmployee(val)}
            toolbar
          />
          {viewMode === 'table' && (
            <CustomDropdown
              options={[
                { value: '', label: 'No Grouping' },
                { value: 'status', label: 'Group by Status' },
                { value: 'urgency', label: 'Group by Urgency' },
                { value: 'client_company', label: 'Group by Company' },
              ]}
              value={groupBy}
              onChange={(val) => setGroupBy(val)}
              toolbar
            />
          )}
          <button
            className={ui.toolbarReset}
            onClick={resetView}
            title="Reset to default view"
          >
            Clear
          </button>
        </div>
      )}

      {/* New Project Form */}
      {showForm && canCreateProjects && (
        <FormModal
          title="New Project"
          milestones={milestones}
          error={error}
          onAddMilestoneClick={() => setShowMilestoneForm(true)}
          onCancel={() => {
            setShowForm(false);
            clearForm();
          }}
          onSubmit={handleSubmit}
          tabs={[
            { key: "overview", label: "Overview" },
            { key: "budget", label: "Budget" },
            canManageProjectTeam && { key: "team", label: "Team" },
            { key: "milestones", label: "Milestones" }
          ].filter(Boolean)}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          assignedEmployees={assignedEmployees}
          setAssignedEmployees={setAssignedEmployees}
          availableEmployees={availableEmployees}
          employeeSearch={employeeSearch}
          setEmployeeSearch={setEmployeeSearch}
          overviewReadOnly={false}      // <-- Add this
          teamReadOnly={false}          // <-- Add this 
          fields={activeTab === "overview" ? overviewFields : activeTab === "budget" ? budgetFields : []}
        />
      )}

      {/* Project Details Modal (Read-only view) */}
      {showDetailModal && editableProject && (
        <DetailsModal
          title="Project Details"
          onClose={() => {
            setShowDetailModal(false);
            setEditableProject(null);
            setActiveTab("overview");
          }}
          onEdit={canEditProjects ? () => {
            setShowDetailModal(false);
            setShowEditModal(true);
          } : null}
          onDelete={canDeleteProjects ? async () => {
            const confirmDelete = window.confirm("Are you sure you want to delete this project?");
            if (confirmDelete) {
              try {
                await deleteProject(editableProject.project_id);
                setProjects(prev => prev.filter(p => p.project_id !== editableProject.project_id));
                setShowDetailModal(false);
                setEditableProject(null);
                showToast("Project deleted successfully", "success");
              } catch (err) {
                console.error("Failed to delete project", err);
                showToast(err.message || "Failed to delete project", "error");
              }
            }
          } : null}
          deleteLabel="Delete Project"
          editLabel="Edit Project"
          tabs={[
            { key: "overview", label: "Overview" },
            canViewProjectBudgets && { key: "budget", label: "Budget" },
            { key: "team", label: "Team" },
            { key: "milestones", label: "Milestones" }
          ].filter(Boolean)}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        >
          {/* Project Header */}
          <div className={detailStyles.detailSection}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px',
              padding: '20px',
              background: `linear-gradient(135deg, ${editableProject.color || '#3b82f6'} 0%, ${editableProject.color || '#3b82f6'}dd 100%)`,
              borderRadius: '12px',
              marginBottom: '8px'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '12px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                border: '2px solid rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                flexShrink: 0
              }}>
                📋
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: 'white' }}>
                  {editableProject.project_name || 'Untitled Project'}
                </h3>
                <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                  {editableProject.client_company || editableProject.client_name || 'No client'}
                </p>
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: '6px'
              }}>
                <span style={{
                  backgroundColor: editableProject.urgency === 'High' ? '#ef4444' : 
                                   editableProject.urgency === 'Medium' ? '#f59e0b' : '#22c55e',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  {editableProject.urgency || 'Low'}
                </span>
                <span style={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}>
                  {statuses.find(s => s.status_id === editableProject.status_id)?.name || 'No Status'}
                </span>
              </div>
            </div>
          </div>

          {activeTab === "overview" && (
            <div>
              {/* Client Information */}
              <div className={detailStyles.detailSection}>
                <h4 className={detailStyles.sectionTitle}>Client Information</h4>
                
                <div className={detailStyles.detailRow}>
                  <span className={detailStyles.detailLabel}>Client Name</span>
                  <span className={`${detailStyles.detailValue} ${!editableProject.client_name ? detailStyles.empty : ''}`}>
                    {editableProject.client_name || '—'}
                  </span>
                </div>

                <div className={detailStyles.detailRow}>
                  <span className={detailStyles.detailLabel}>Company</span>
                  <span className={`${detailStyles.detailValue} ${!editableProject.client_company ? detailStyles.empty : ''}`}>
                    {editableProject.client_company || '—'}
                  </span>
                </div>

                <div className={detailStyles.detailRow}>
                  <span className={detailStyles.detailLabel}>Email</span>
                  <span className={`${detailStyles.detailValue} ${!editableProject.email ? detailStyles.empty : ''}`}>
                    {editableProject.email ? (
                      <a href={`mailto:${editableProject.email}`} style={{ color: '#6366f1', textDecoration: 'none' }}>
                        {editableProject.email}
                      </a>
                    ) : '—'}
                  </span>
                </div>

                <div className={detailStyles.detailRow}>
                  <span className={detailStyles.detailLabel}>Phone</span>
                  <span className={`${detailStyles.detailValue} ${!editableProject.phone_number ? detailStyles.empty : ''}`}>
                    {editableProject.phone_number || '—'}
                  </span>
                </div>
              </div>

              {/* Project Details */}
              <div className={detailStyles.detailSection}>
                <h4 className={detailStyles.sectionTitle}>Project Details</h4>

                <div className={detailStyles.detailRow}>
                  <span className={detailStyles.detailLabel}>Timeline</span>
                  <span className={detailStyles.detailValue}>
                    {editableProject.start_date || editableProject.end_date ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ 
                          backgroundColor: '#dbeafe', 
                          color: '#1e40af', 
                          padding: '4px 10px', 
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          fontWeight: '500'
                        }}>
                          {editableProject.start_date?.slice(0, 10) || 'No start'}
                        </span>
                        <span style={{ color: '#9ca3af' }}>→</span>
                        <span style={{ 
                          backgroundColor: '#dcfce7', 
                          color: '#166534', 
                          padding: '4px 10px', 
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          fontWeight: '500'
                        }}>
                          {editableProject.end_date?.slice(0, 10) || 'No end'}
                        </span>
                      </div>
                    ) : <em className={detailStyles.empty}>No dates set</em>}
                  </span>
                </div>

                <div className={detailStyles.detailRow}>
                  <span className={detailStyles.detailLabel}>Labels</span>
                  <span className={detailStyles.detailValue}>
                    {editLabels.length > 0 ? (
                      <div className={detailStyles.labelsList}>
                        {editLabels.map(label => (
                          <span 
                            key={label.label_id} 
                            className={detailStyles.labelTag}
                            style={{ backgroundColor: label.color || '#6b7280' }}
                          >
                            {label.name}
                          </span>
                        ))}
                      </div>
                    ) : <em className={detailStyles.empty}>No labels</em>}
                  </span>
                </div>

                <div className={detailStyles.detailRow}>
                  <span className={detailStyles.detailLabel}>Location</span>
                  <span className={`${detailStyles.detailValue} ${!editableProject.location ? detailStyles.empty : ''}`}>
                    {editableProject.location || '—'}
                  </span>
                </div>

                <div className={detailStyles.detailRow}>
                  <span className={detailStyles.detailLabel}>Address</span>
                  <span className={`${detailStyles.detailValue} ${!editableProject.address ? detailStyles.empty : ''}`}>
                    {editableProject.address ? (
                      <div className={detailStyles.descriptionText}>{editableProject.address}</div>
                    ) : '—'}
                  </span>
                </div>

                <div className={detailStyles.detailRow}>
                  <span className={detailStyles.detailLabel}>Description</span>
                  <span className={`${detailStyles.detailValue} ${!editableProject.description ? detailStyles.empty : ''}`}>
                    {editableProject.description ? (
                      <div className={detailStyles.descriptionText}>{editableProject.description}</div>
                    ) : 'No description'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {activeTab === "team" && (
            <div>
              <div className={detailStyles.detailSection}>
                <h4 className={detailStyles.sectionTitle}>
                  Team Members
                  {assignedEmployees.length > 0 && (
                    <span style={{ 
                      marginLeft: '8px', 
                      backgroundColor: '#e5e7eb', 
                      color: '#374151', 
                      padding: '2px 8px', 
                      borderRadius: '10px',
                      fontSize: '0.75rem',
                      fontWeight: '500'
                    }}>
                      {assignedEmployees.length}
                    </span>
                  )}
                </h4>
                
                {assignedEmployees.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {assignedEmployees.map(emp => (
                      <div key={emp.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.9rem',
                          fontWeight: '600'
                        }}>
                          {emp.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', color: '#111827' }}>{emp.name}</div>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                            {emp.role || emp.job_title || 'Team Member'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#9ca3af', fontStyle: 'italic', textAlign: 'center', padding: '24px', margin: 0 }}>
                    No team members assigned to this project.
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === "milestones" && (
            <div>
              <div className={detailStyles.detailSection}>
                <h4 className={detailStyles.sectionTitle}>
                  Milestones
                  {milestones.length > 0 && (
                    <span style={{ 
                      marginLeft: '8px', 
                      backgroundColor: '#e5e7eb', 
                      color: '#374151', 
                      padding: '2px 8px', 
                      borderRadius: '10px',
                      fontSize: '0.75rem',
                      fontWeight: '500'
                    }}>
                      {milestones.length}
                    </span>
                  )}
                </h4>
                
                {milestones.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {milestones.map(milestone => (
                      <div key={milestone.milestone_id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '14px 16px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div>
                          <div style={{ fontWeight: '500', color: '#111827' }}>{milestone.milestone_name}</div>
                          {milestone.due_date && (
                            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '2px' }}>
                              Due: {milestone.due_date?.slice(0, 10)}
                            </div>
                          )}
                        </div>
                        <span style={{
                          backgroundColor: milestone.status === 'completed' ? '#dcfce7' :
                                          milestone.status === 'in_progress' ? '#dbeafe' : '#f3f4f6',
                          color: milestone.status === 'completed' ? '#166534' :
                                 milestone.status === 'in_progress' ? '#1e40af' : '#374151',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          textTransform: 'capitalize'
                        }}>
                          {milestone.status?.replace('_', ' ') || 'Open'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#9ca3af', fontStyle: 'italic', textAlign: 'center', padding: '24px', margin: 0 }}>
                    No milestones created for this project.
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === "budget" && canViewProjectBudgets && (
            <div>
              <div className={detailStyles.detailSection}>
                <h4 className={detailStyles.sectionTitle}>Budget Overview</h4>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    padding: '20px',
                    backgroundColor: '#f0fdf4',
                    borderRadius: '12px',
                    border: '1px solid #bbf7d0'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: '#166534', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>
                      Estimated Budget
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#166534' }}>
                      {budgetCost ? `$${parseFloat(budgetCost).toLocaleString()}` : '—'}
                    </div>
                  </div>
                  <div style={{
                    padding: '20px',
                    backgroundColor: '#eff6ff',
                    borderRadius: '12px',
                    border: '1px solid #bfdbfe'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: '#1e40af', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>
                      Estimated Hours
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e40af' }}>
                      {budgetHours ? `${budgetHours} hrs` : '—'}
                    </div>
                  </div>
                </div>

                <div className={detailStyles.detailRow}>
                  <span className={detailStyles.detailLabel}>Notes</span>
                  <span className={`${detailStyles.detailValue} ${!budgetNotes ? detailStyles.empty : ''}`}>
                    {budgetNotes || 'No budget notes'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </DetailsModal>
      )}

      {/* Edit Project Modal */}
      {showEditModal && editableProject && (
        <FormModal
          milestones={milestones}
          setMilestones={setMilestones}
          onMilestoneClick={handleEditMilestone}
          title="Edit Project"
          error={null}
          overviewReadOnly={false}
          onCancel={() => {
            setShowEditModal(false);
            setEditableProject(null);
            setActiveTab("overview");
          }}
          onSubmit={handleUpdateProject}
          onAddMilestoneClick={() => setShowMilestoneForm(true)}
          tabs={[
            { key: "overview", label: "Overview" },
            canViewProjectBudgets && { key: "budget", label: "Budget" },
            { key: "team", label: "Team" }, // Always show Team tab
            { key: "milestones", label: "Milestones" }
          ].filter(Boolean)}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          assignedEmployees={assignedEmployees}
          setAssignedEmployees={setAssignedEmployees}
          availableEmployees={availableEmployees}
          employeeSearch={employeeSearch}
          setEmployeeSearch={setEmployeeSearch}
          // Pass read-only flag to FormModal
          teamReadOnly={false}
          fields={
            activeTab === "overview" 
              ? [
                  { 
                    section: "overview", 
                    label: "Project Name", 
                    type: "custom",
                    render: () => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <input
                          type="text"
                          value={editableProject.project_name || ''}
                          onChange={(e) => setEditableProject({ ...editableProject, project_name: e.target.value })}
                          style={{ flex: 1 }}
                        />
                        <ColorPicker
                          value={editableProject.color || '#3b82f6'}
                          onChange={(e) => setEditableProject({ ...editableProject, color: e.target.value })}
                        />
                      </div>
                    )
                  },
                  { 
                    section: "overview", 
                    label: "Client Details", 
                    type: "custom",
                    render: () => (
                      <div style={{ display: 'flex', gap: '16px' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Client Name</label>
                          <input
                            type="text"
                            value={editableProject.client_name || ''}
                            onChange={(e) => handleEditNameInput(e.target.value)}
                            onFocus={handleEditNameFocus}
                            onBlur={() => setTimeout(() => setShowNameDropdown(false), 200)}
                            style={{ width: '100%' }}
                          />
                          {nameSuggestions.length > 0 && showNameDropdown && (
                            <ul style={{ position: 'absolute', zIndex: 10, background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', listStyle: 'none', padding: '4px 0', margin: '4px 0 0', width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                              {nameSuggestions.map((s, i) => (
                                <li key={i} onClick={() => handleEditNameSelect(s)} style={{ padding: '8px 12px', cursor: 'pointer' }}>{s}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div style={{ flex: 1, position: 'relative' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Company Name</label>
                          <input
                            type="text"
                            value={editableProject.client_company || ''}
                            onChange={(e) => handleEditCompanyInput(e.target.value)}
                            onFocus={handleEditCompanyFocus}
                            onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 200)}
                            style={{ width: '100%' }}
                          />
                          {companySuggestions.length > 0 && showCompanyDropdown && (
                            <ul style={{ position: 'absolute', zIndex: 10, background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', listStyle: 'none', padding: '4px 0', margin: '4px 0 0', width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                              {companySuggestions.map((s, i) => (
                                <li key={i} onClick={() => handleEditCompanySelect(s)} style={{ padding: '8px 12px', cursor: 'pointer' }}>{s}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )
                  },
                  { section: "overview", label: "Email", value: editableProject.email, onChange: (e) => setEditableProject({ ...editableProject, email: e.target.value }), type: "email" },
                  { section: "overview", label: "Phone Number", value: editableProject.phone_number, onChange: (e) => setEditableProject({ ...editableProject, phone_number: e.target.value }), type: "tel" },
                  { 
                    section: "overview", 
                    label: "Labels", 
                    type: "custom",
                    render: () => (
                      <LabelSelector
                        selectedLabels={editLabels}
                        onChange={setEditLabels}
                        placeholder="Add service type labels..."
                      />
                    )
                  },
                  { 
                    section: "overview", 
                    label: "Project Dates", 
                    type: "custom",
                    render: () => (
                      <div style={{ display: 'flex', gap: '16px' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Start Date</label>
                          <input
                            type="date"
                            value={editableProject.start_date?.slice(0, 10) || ''}
                            onChange={(e) => setEditableProject({ ...editableProject, start_date: e.target.value })}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>End Date</label>
                          <input
                            type="date"
                            value={editableProject.end_date?.slice(0, 10) || ''}
                            onChange={(e) => setEditableProject({ ...editableProject, end_date: e.target.value })}
                            style={{ width: '100%' }}
                          />
                        </div>
                      </div>
                    )
                  },
                  { 
                    section: "overview", 
                    label: "Project Status", 
                    type: "custom",
                    render: () => (
                      <div style={{ display: 'flex', gap: '16px' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Urgency</label>
                          <CustomDropdown
                            options={[
                              { value: 'Low', label: 'Low' },
                              { value: 'Medium', label: 'Medium' },
                              { value: 'High', label: 'High' },
                            ]}
                            value={editableProject.urgency || 'Medium'}
                            onChange={(val) => setEditableProject({ ...editableProject, urgency: val })}
                            fullWidth
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Status</label>
                          <StatusDropdown
                            statuses={statuses}
                            value={editableProject.status_id || null}
                            onChange={(id) => setEditableProject({ ...editableProject, status_id: id })}
                            onAddNew={() => { setAddStatusContext('edit'); setShowAddStatusModal(true); }}
                            fullWidth
                          />
                        </div>
                      </div>
                    )
                  },
                  { 
                    section: "overview", 
                    label: "Location & Address", 
                    type: "custom",
                    render: () => (
                      <div style={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Location</label>
                          <input
                            type="text"
                            value={editableProject.location || ''}
                            onChange={(e) => setEditableProject({ ...editableProject, location: e.target.value })}
                            placeholder="e.g., New York, NY"
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Address</label>
                          <textarea
                            value={editableProject.address || ''}
                            onChange={(e) => setEditableProject({ ...editableProject, address: e.target.value })}
                            placeholder="Full street address"
                            style={{ width: '100%', minHeight: '60px' }}
                          />
                        </div>
                      </div>
                    )
                  },
                  { section: "overview", label: "Description", value: editableProject.description, onChange: (e) => setEditableProject({ ...editableProject, description: e.target.value }), type: "textarea" },
                ]
              : activeTab === "budget" 
                ? budgetFields
                : []
          }
          customTabContent={
            activeTab === "budget" && canViewProjectBudgets ? renderBudgetSummary() : null
          }
        />
      )}

      {/* Budget Edit Modal */}
      {showBudgetEditModal && canManageProjectBudgets && (
        <div className={ui.modalOverlay} style={{ zIndex: 1000001 }}>
          <div className={ui.modal}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              background: '#f9fafb'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}>
                Edit Budget
              </h2>
              <button
                type="button"
                onClick={() => setShowBudgetEditModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ×
              </button>
            </div>

            <div className={ui.modalContent}>
              <div className={ui.inputGroup}>
                <label>Estimated Budget ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={editableBudget.budget_cost}
                  onChange={(e) => setEditableBudget({ ...editableBudget, budget_cost: e.target.value })}
                />
              </div>

              <div className={ui.inputGroup}>
                <label>Estimated Hours</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  placeholder="0"
                  value={editableBudget.budget_hours}
                  onChange={(e) => setEditableBudget({ ...editableBudget, budget_hours: e.target.value })}
                />
              </div>

              <div className={ui.inputGroup}>
                <label>Budget Notes</label>
                <textarea
                  placeholder="Additional notes about the budget..."
                  value={editableBudget.notes}
                  onChange={(e) => setEditableBudget({ ...editableBudget, notes: e.target.value })}
                  rows={4}
                />
              </div>
            </div>

            <div className={ui.actions}>
              <button
                type="button"
                className={ui.cancelButton}
                onClick={() => setShowBudgetEditModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={ui.saveButton}
                onClick={handleSaveBudget}
              >
                Save Budget
              </button>
            </div>
          </div>
        </div>
      )}

      {showMilestoneForm && (
        <div className={ui.modalOverlay} style={{ zIndex: 1000001 }}>
          <div className={ui.modal}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              background: '#f9fafb'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}>
                Add Milestone
              </h2>
              <button
                type="button"
                onClick={() => setShowMilestoneForm(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className={ui.modalContent}>
              <div className={ui.inputGroup}>
                <label>Milestone Name</label>
                <input
                  type="text"
                  value={milestoneFormData.milestone_name}
                  onChange={(e) => setMilestoneFormData({ ...milestoneFormData, milestone_name: e.target.value })}
                />
              </div>

              <div className={ui.inputGroup}>
                <label>Milestone Dates</label>
                <div className={ui.inputRow}>
                  <div className={ui.inputGroup}>
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={milestoneFormData.start_date}
                      onChange={(e) => setMilestoneFormData({ ...milestoneFormData, start_date: e.target.value })}
                    />
                  </div>
                  <div className={ui.inputGroup}>
                    <label>Due Date</label>
                    <input
                      type="date"
                      value={milestoneFormData.due_date}
                      onChange={(e) => setMilestoneFormData({ ...milestoneFormData, due_date: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className={ui.inputGroup}>
                <label>Status</label>
                <CustomDropdown
                  options={[
                    { value: 'open', label: 'Open' },
                    { value: 'in progress', label: 'In Progress' },
                    { value: 'completed', label: 'Completed' },
                  ]}
                  value={milestoneFormData.status}
                  onChange={(val) => setMilestoneFormData({ ...milestoneFormData, status: val })}
                  fullWidth
                />
              </div>

              <div className={ui.inputGroup}>
                <label>Description</label>
                <textarea
                  value={milestoneFormData.description}
                  onChange={(e) => setMilestoneFormData({ ...milestoneFormData, description: e.target.value })}
                />
              </div>
            </div>

            <div className={ui.actions}>
              <button
                type="button"
                className={ui.cancelButton}
                onClick={() => setShowMilestoneForm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={ui.saveButton}
                onClick={async () => {
                  if (!editableProject?.project_id) {
                    showToast("Please save the project first before adding milestones", "error");
                    return;
                  }

                  if (!milestoneFormData.milestone_name?.trim()) {
                    showToast("Milestone name is required", "error");
                    return;
                  }

                  // Validate dates: start date should be same or earlier than due date
                  if (milestoneFormData.start_date && milestoneFormData.due_date) {
                    const startDate = new Date(milestoneFormData.start_date);
                    const dueDate = new Date(milestoneFormData.due_date);
                    if (startDate > dueDate) {
                      showToast("Start date cannot be later than due date", "error");
                      return;
                    }
                  }

                  try {
                    const newMilestone = await createMilestone(editableProject.project_id, {
                      ...milestoneFormData,
                    });

                    setMilestones(prev => [...prev, newMilestone]);

                    // Refresh projects list to show the new milestone
                    await refreshProjects();

                    // reset form
                    setMilestoneFormData({
                      milestone_name: "",
                      description: "",
                      start_date: "",
                      due_date: "",
                      status: "open",
                    });
                    setShowMilestoneForm(false);
                    showToast("Milestone created successfully", "success");
                  } catch (err) {
                    console.error("Failed to create milestone:", err);
                    showToast(err.message || "Failed to create milestone", "error");
                  }
                }}
              >
                Save Milestone
              </button>
            </div>
          </div>
        </div>
      )}

      {showMilestoneEditModal && editableMilestone && (
        <div className={ui.modalOverlay} style={{ zIndex: 1000001 }}>
          <div className={ui.modal}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              background: '#f9fafb'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}>
                Edit Milestone
              </h2>
              <button
                type="button"
                onClick={() => setShowMilestoneEditModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ×
              </button>
            </div>
            
            {/* Tabs */}
            <div className={ui.modalTabs}>
              {["Overview", "Team"].map(tab => (
                <button
                  key={tab}
                  className={`${ui.tabButton} ${activeMilestoneTab === tab ? ui.activeTab : ""}`}
                  onClick={() => setActiveMilestoneTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className={ui.modalContent}>
              {/* === Overview Tab === */}
              {activeMilestoneTab === "Overview" && (
                <>
                  <div className={ui.inputGroup}>
                    <label>Name</label>
                    <input
                      type="text"
                      value={editableMilestone.milestone_name}
                      onChange={(e) =>
                        setEditableMilestone((prev) => ({
                          ...prev,
                          milestone_name: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className={ui.inputGroup}>
                    <label>Milestone Dates</label>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Start Date</label>
                        <input
                          type="date"
                          value={editableMilestone.start_date?.slice(0, 10) || ""}
                          onChange={(e) =>
                            setEditableMilestone((prev) => ({
                              ...prev,
                              start_date: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Due Date</label>
                        <input
                          type="date"
                          value={editableMilestone.due_date?.slice(0, 10) || ""}
                          onChange={(e) =>
                            setEditableMilestone((prev) => ({
                              ...prev,
                              due_date: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className={ui.inputGroup}>
                    <label>Status</label>
                    <CustomDropdown
                      options={[
                        { value: 'open', label: 'Open' },
                        { value: 'in progress', label: 'In Progress' },
                        { value: 'completed', label: 'Completed' },
                      ]}
                      value={editableMilestone.status}
                      onChange={(val) =>
                        setEditableMilestone((prev) => ({
                          ...prev,
                          status: val,
                        }))
                      }
                      fullWidth
                    />
                  </div>

                  <div className={ui.inputGroup}>
                    <label>Description</label>
                    <textarea
                      value={editableMilestone.description || ""}
                      onChange={(e) =>
                        setEditableMilestone((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                    />
                  </div>
                </>
              )}

              {/* === Team Tab === */}
              {activeMilestoneTab === "Team" && (
                <>
                  <div className={ui.inputGroup}>
                    <label>Search people by name or job title</label>
                    <input
                      type="text"
                      placeholder="Search..."
                      value={milestoneEmployeeSearch}
                      onChange={(e) => setMilestoneEmployeeSearch(e.target.value)}
                      onFocus={() => setMilestoneSearchFocused(true)}
                      onBlur={() => setTimeout(() => setMilestoneSearchFocused(false), 200)}
                    />
                  </div>

                  {/* Filtered results */}
                  {milestoneSearchFocused && (
                    <div className={ui.searchResults}>
                      {availableEmployees
                        .filter(emp =>
                          (emp.name + emp.job_title).toLowerCase().includes(milestoneEmployeeSearch.toLowerCase())
                        )
                        .filter(emp =>
                          !editableMilestone.team_members?.some(m => m.id === emp.id)
                        )
                        .map(emp => (
                          <div
                            key={emp.id}
                            className={ui.searchItem}
                            onClick={() => {
                              const newMembers = [
                                ...(editableMilestone.team_members || []),
                                { ...emp, role: "" }
                              ];
                              setEditableMilestone(prev => ({
                                ...prev,
                                team_members: newMembers
                              }));
                              setMilestoneEmployeeSearch("");
                            }}
                          >
                            {emp.name} — {emp.job_title}
                          </div>
                        ))}
                    </div>
                  )}

                  {editableMilestone.team_members?.length > 0 && (
                    <div className={ui.assignedList}>
                      {editableMilestone.team_members.map((emp, idx) => (
                        <div key={emp.id} className={ui.assignedItem}>
                          <span>{emp.name}</span>
                          <input
                            type="text"
                            placeholder="Role in milestone"
                            value={emp.role || ""}
                            onChange={(e) => {
                              const updated = [...editableMilestone.team_members];
                              updated[idx].role = e.target.value;
                              setEditableMilestone(prev => ({
                                ...prev,
                                team_members: updated
                              }));
                            }}
                          />
                          <button
                            type="button"
                            className={ui.removeButton}
                            onClick={() => {
                              const filtered = editableMilestone.team_members.filter(e => e.id !== emp.id);
                              setEditableMilestone(prev => ({
                                ...prev,
                                team_members: filtered
                              }));
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Actions */}
            <div className={ui.actions}>
              <button
                type="button"
                className={ui.cancelButton}
                onClick={() => setShowMilestoneEditModal(false)}
              >
                Close
              </button>
              <button
                type="button"
                className={ui.saveButton}
                onClick={handleMilestoneAssignmentSave}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <FormModal
          title="Export Projects to CSV"
          onCancel={() => setShowExportModal(false)}
          onSubmit={handleExportSubmit}
          fields={[
            {
              label: "Date Range (optional)",
              type: "custom",
              render: () => (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={exportForm.startDate}
                      onChange={(e) =>
                        setExportForm((prev) => ({ ...prev, startDate: e.target.value }))
                      }
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '11px',
                        background: 'white',
                        color: '#334155',
                      }}
                    />
                  </div>
                  <div style={{ 
                    color: '#94a3b8', 
                    fontSize: '1.2rem',
                    paddingBottom: '6px'
                  }}>→</div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
                      End Date
                    </label>
                    <input
                      type="date"
                      value={exportForm.endDate}
                      onChange={(e) =>
                        setExportForm((prev) => ({ ...prev, endDate: e.target.value }))
                      }
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '11px',
                        background: 'white',
                        color: '#334155',
                      }}
                    />
                  </div>
                </div>
              ),
            },
            {
              label: "Status (optional)",
              type: "select",
              value: exportForm.statusId,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, statusId: e.target.value })),
              options: [
                { value: "", label: "All statuses" },
                ...(statuses || []).map((s) => ({
                  value: s.status_id,
                  label: s.name
                }))
              ],
            },
            {
              label: "Urgency (optional)",
              type: "select",
              value: exportForm.urgency,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, urgency: e.target.value })),
              options: [
                { value: "", label: "All urgencies" },
                { value: "Low", label: "Low" },
                { value: "Medium", label: "Medium" },
                { value: "High", label: "High" }
              ],
            },
            {
              label: "Group By (optional)",
              type: "select",
              value: exportForm.groupBy,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, groupBy: e.target.value })),
              options: [
                { value: "", label: "No grouping" },
                { value: "company", label: "Client Company" },
                { value: "contact", label: "Client Name" }
              ],
            },
            {
              type: "custom",
              render: () => (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportForm.includeMilestones}
                    onChange={(e) =>
                      setExportForm((prev) => ({ ...prev, includeMilestones: e.target.checked }))
                    }
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3a6aed',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#334155' }}>Include milestones in export</span>
                </label>
              ),
            },
            {
              type: "custom",
              render: () => (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportForm.includeAssignments}
                    onChange={(e) =>
                      setExportForm((prev) => ({ ...prev, includeAssignments: e.target.checked }))
                    }
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3a6aed',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#334155' }}>Include team members in export</span>
                </label>
              ),
            },
            ...(canViewProjectBudgets ? [{
              type: "custom",
              render: () => (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportForm.includeBudget}
                    onChange={(e) =>
                      setExportForm((prev) => ({ ...prev, includeBudget: e.target.checked }))
                    }
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3a6aed',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#334155' }}>Include budget information in export</span>
                </label>
              ),
            }] : []),
          ].filter(Boolean)}
        />
      )}

      {/* Table */}
      <div className={ui.dataList}>
        {/* <h2>{isViewingAssignedOnly ? 'My Projects' : 'Projects'}</h2> */}
        {showLoading ? (
          <div className={ui.tableSkeleton}>
            <div className={`${ui.skeletonBlock} ${ui.tableSkeletonHeader}`} />
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={`projects-skeleton-row-${idx}`} className={`${ui.skeletonBlock} ${ui.tableSkeletonRow}`} />
            ))}
          </div>
        ) : displayedProjects.length === 0 ? (
          <div className={`${ui.emptyState} ${ui.fadeInContent}`}>
            <img
              src="\projects_empty.svg"
              alt="No projects"
              className={ui.emptyStateImage}
            />
            <h3>{showArchive ? (hasActiveFilters ? 'No matching archived projects' : 'No archived projects') : (isViewingAssignedOnly ? 'No assigned projects' : hasActiveFilters ? 'No matching projects' : 'No active projects')}</h3>
            <p>{showArchive ? (hasActiveFilters ? 'Try adjusting your search or filters' : 'Completed projects will appear here') : (isViewingAssignedOnly ? 'You have not been assigned to any projects yet' : hasActiveFilters ? 'Try adjusting your search or filters' : 'Create a new project to get started')}</p>
            {canCreateProjects && !hasActiveFilters && !showArchive && (
              <button 
                className={ui.addButton} 
                onClick={() => setShowForm(true)}
              >
                <img src={plusIcon} alt="" width="16" height="16" />
                Add Project
              </button>
            )}
          </div>
        ) : statuses.length > 0 && viewMode === 'gantt' ? (
          <div className={ui.fadeInContent}>
            <GanttView
              projects={displayedProjects}
              canEditProjects={canEditProjects}
              onProjectClick={openProjectDetail}
              onMilestoneClick={handleEditMilestone}
              onProjectDateChange={async (projectId, dates) => {
                try {
                  await updateProject(projectId, dates);
                  await refreshProjects();
                  showToast("Project dates updated", "success");
                } catch (err) {
                  console.error("Failed to update project dates:", err);
                  showToast(err.message || "Failed to update project dates", "error");
                }
              }}
              onMilestoneDateChange={async (milestoneId, dates) => {
                try {
                  await updateMilestone(milestoneId, dates);
                  await refreshProjects();
                  showToast("Milestone dates updated", "success");
                } catch (err) {
                  console.error("Failed to update milestone dates:", err);
                  showToast(err.message || "Failed to update milestone dates", "error");
                }
              }}
            />
          </div>
        ) : statuses.length > 0 && (
          <div className={ui.fadeInContent}>
            <DataTable
              columns={columns}
              data={displayedProjects}
              groupedBy={getGroupByValue()}
              collapsedGroups={collapsedGroups}
              groupOrder={groupBy === "status" ? statuses.filter(s => showArchive
                ? s.name?.toLowerCase() === 'completed'
                : s.name?.toLowerCase() !== 'completed'
              ).map(s => s.name) : []}
              toggleGroupCollapse={toggleGroupCollapse}
              onRowClick={openProjectDetail}
              onMilestoneClick={handleEditMilestone}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              customCellRender={{
                status: (project) => showArchive ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{statuses.find(s => s.status_id === project.status_id)?.name || 'Completed'}</span>
                    {canEditProjects && (
                      <button
                        className={ui.restoreButton}
                        onClick={(e) => { e.stopPropagation(); handleRestoreProject(project); }}
                      >
                        Restore
                      </button>
                    )}
                  </div>
                ) : (
                  <StatusDropdown
                    statuses={statuses}
                    value={project.status_id}
                    onChange={(id) => handleStatusChange(project, id)}
                    onAddNew={() => { setAddStatusContext('table'); setShowAddStatusModal(true); }}
                    disabled={!canEditProjects}
                    compact
                  />
                ),
                urgency: (project) => (
                  <CustomDropdown
                    options={[
                      { value: 'Low', label: 'Low' },
                      { value: 'Medium', label: 'Medium' },
                      { value: 'High', label: 'High' },
                    ]}
                  value={project.urgency}
                  onChange={(val) => handleUrgencyChange(project, val)}
                  disabled={!canEditProjects}
                  compact
                />
              ),
              start_date: (project) =>
                project.start_date 
                  ? new Date(project.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : "—",
              end_date: (project) =>
                project.end_date 
                  ? new Date(project.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : "—",
              labels: (project) => {
                if (project.labels && project.labels.length > 0) {
                  return (
                    <div className={detailStyles.labelsList}>
                      {project.labels.map(label => (
                        <span 
                          key={label.label_id} 
                          className={detailStyles.labelTag}
                          style={{ backgroundColor: label.color || '#6b7280' }}
                        >
                          {label.name}
                        </span>
                      ))}
                    </div>
                  );
                }
                return "—";
              },
            }}
            expandedRows={expandedRows}
            toggleRowExpand={toggleRowExpand}
          />
          </div>
        )}
      </div>

      {showArchiveConfirm && (
        <ConfirmDialog
          message="Are you sure this project is complete? It will be archived and removed from the active projects list."
          confirmText="Yes, Archive"
          cancelText="Cancel"
          onConfirm={async () => {
            setShowArchiveConfirm(false);
            if (pendingArchiveAction) {
              await pendingArchiveAction();
              setPendingArchiveAction(null);
            }
          }}
          onCancel={() => {
            setShowArchiveConfirm(false);
            setPendingArchiveAction(null);
          }}
        />
      )}

      {showAddStatusModal && (
        <AddStatusModal
          onSubmit={handleCreateStatus}
          onCancel={() => {
            setShowAddStatusModal(false);
            setAddStatusContext(null);
          }}
        />
      )}

      <ReorderListModal
        isOpen={showStatusReorderModal}
        title="Reorder Project Statuses"
        description="Drag statuses to control their display order across project screens and filters."
        items={statuses}
        getId={(item) => item.status_id}
        getLabel={(item) => item.name}
        getColor={(item) => item.color}
        onClose={() => setShowStatusReorderModal(false)}
        onSave={handleSaveStatusOrder}
        saving={savingStatusOrder}
      />
    </div>
  );
}
